import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Link, 
  Trash2, 
  Edit3, 
  Eye, 
  Copy, 
  Check,
  BarChart,
  ExternalLink,
  Power
} from 'lucide-react';
import config from './config';
import './FlowList.css';

const FlowList = () => {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    try {
      const response = await fetch(`${config.serverUrl}/api/flows/list/default-user`);
      if (response.ok) {
        const data = await response.json();
        setFlows(data.flows || []);
      }
    } catch (error) {
      console.error('Erro ao carregar flows:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewFlow = () => {
    navigate('/flow/builder/new');
  };

  const editFlow = (id) => {
    navigate(`/flow/builder/${id}`);
  };

  const viewFlow = (url) => {
    window.open(`/${url}`, '_blank');
  };

  const copyLink = async (url, id) => {
    const fullUrl = `${config.clientUrl}/${url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      alert(`Link: ${fullUrl}`);
    }
  };

  const deleteFlow = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir este flow?')) return;

    try {
      const response = await fetch(`${config.serverUrl}/api/flows/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setFlows(flows.filter(f => f._id !== id));
      }
    } catch (error) {
      console.error('Erro ao deletar flow:', error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flow-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="flow-container">
      <div className="flow-header">
        <h1>Link2.do Flow</h1>
        <button onClick={createNewFlow} className="btn btn-primary">
          <Plus size={20} />
          Novo Flow
        </button>
      </div>

      <div className="flow-content">
        {flows.length === 0 ? (
          <div className="empty-state">
            <Link size={64} />
            <h2>Nenhum flow criado</h2>
            <p>Crie seu primeiro flow e compartilhe com o mundo</p>
            <button onClick={createNewFlow} className="btn btn-primary btn-large">
              <Plus size={24} />
              Criar Flow
            </button>
          </div>
        ) : (
          <div className="flows-grid">
            {flows.map((flow) => (
              <div key={flow._id} className="flow-card">
                <div className="flow-card-header">
                  <h3>{flow.name}</h3>
                  <div className={`flow-status ${flow.isActive ? 'active' : 'inactive'}`}>
                    <Power size={14} />
                    {flow.isActive ? 'Ativo' : 'Inativo'}
                  </div>
                </div>
                
                <div className="flow-url">
                  <Link size={14} />
                  <a href={`/${flow.url}`} target="_blank" rel="noopener noreferrer">
                    /{flow.url}
                  </a>
                </div>

                <div className="flow-stats">
                  <div className="stat">
                    <Eye size={16} />
                    <span>{flow.analytics?.views || 0} views</span>
                  </div>
                  <div className="stat">
                    <BarChart size={16} />
                    <span>{flow.analytics?.submissions || 0} envios</span>
                  </div>
                </div>

                <div className="flow-meta">
                  <span className="flow-type">{flow.type === 'form' ? 'Formulário' : 'Links'}</span>
                  <span className="flow-date">{formatDate(flow.updatedAt)}</span>
                </div>

                <div className="flow-actions">
                  <button
                    onClick={() => editFlow(flow._id)}
                    className="btn-action"
                    title="Editar"
                  >
                    <Edit3 size={18} />
                  </button>
                  
                  <button
                    onClick={() => viewFlow(flow.url)}
                    className="btn-action"
                    title="Visualizar"
                  >
                    <ExternalLink size={18} />
                  </button>
                  
                  <button
                    onClick={() => copyLink(flow.url, flow._id)}
                    className="btn-action"
                    title="Copiar link"
                  >
                    {copiedId === flow._id ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                  
                  <button
                    onClick={(e) => deleteFlow(flow._id, e)}
                    className="btn-action btn-danger"
                    title="Excluir"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowList;